import { cond, T, always, pathOr, forEach, reduce, equals, filter, toLower, path, find, propEq, gt, not, includes, ascend, compose, sortWith, map, split } from 'ramda';
import {
  USERS_WORKS_ROUTE,
  USERS_OVERVIEW_BASE_ROUTE,
} from 'src/route_constants';
import { isMortgage } from './Advisors/utilities';
import { authenticatedUser, currentAppSession, unAuthenticatedUser } from 'src/utilities/storage';
import { Task } from "src/types/Task";
import { ASSIGNEE_NAME, ASSIGNOR_NAME, META_DATA, STATUS_COMPLETED } from 'src/services/constants';
import { isEmptyOrNil, isNotEmpty, returnIsNotEmptyOrTrue } from 'src/utilities/hasValue';
import { capitalizeAllWords } from 'src/utilities/capitalize';
import { USER_ONBOARD_APPLICANT } from 'src/services/constants/accounts';
import { MIN_MODERATE_STRONG, MIN_STRONG } from 'src/utilities/notifications/accounts';
import { buildLabel } from 'src/utilities/naming';

export const taskDasboardURL = (thisLoanType: string, _loanId?: string) => {
  return cond([
    [isMortgage, always(`${USERS_WORKS_ROUTE}`)],
    [T, always(dashboardURL(thisLoanType))],
  ])(thisLoanType);
};

export const dashboardURL = (thisLoanType: string) => {
  return `${USERS_OVERVIEW_BASE_ROUTE}&thisLoanType=${toLower(
    thisLoanType
  )}`;
};



/**
 partitionArray returns two arrays which are split from a single array based on a condition
 @param array { array } array to be divided
 @param callback { function } the condition function based on which the array needs to be split
 */
export const partitionArray = (array: any, callback: any) => {
  const matches: any = []
  const nonMatches: any = []

  // push each element into array depending on return value of `callback`
  forEach((element: any) => (callback(element) ? matches : nonMatches).push(element), array)

  return [matches, nonMatches]
}

export const createMarkup = (data: any) => ({ __html: data });


/**
 createDefaultState creates default state considering the previous state of the question
 @param array { array } data bag containing all fields
 */
export const createDefaultState = (array: any[]) => {
  return reduce((a, v): any => ({ ...a, [v.name]: v.value }), {}, array);
};





export const notAuthenticated = () => {
  const accountId = authenticatedUser.accountId.get()
  return isEmptyOrNil(accountId)
}

/**
 * notHasWorkflowLoanActive to check is workflow is on before authedication 
*/
export const notHasWorkflowLoanActive = () => {
  const { loanAppId } = currentAppSession();
  const workflowProductType = unAuthenticatedUser.productType.get()
  return isNotEmpty(workflowProductType) && isEmptyOrNil(loanAppId)
}


export const flattenTask = (task: Task) => {
  const comment = pathOr('', ['comment'], task) || pathOr('', ['asks', 'filled', '0000_EXPLANATION_TITLE'], task)
  const createdAt = pathOr('', ['created_at'], task)
  const dueDate = pathOr('', ['due_date'], task)
  const id = pathOr('', ['id'], task)
  const status = pathOr('', ['status', 'phase'], task)
  const assigneeName = pathOr('', [META_DATA, ASSIGNEE_NAME], task)
  const assignorName = pathOr('', [META_DATA, ASSIGNOR_NAME], task);
  const documentArtifact = pathOr('', [META_DATA, "lendsmart_sh/document_artifact"], task);
  const documentCategory = pathOr('', [META_DATA, "lendsmart_sh/document_category"], task);
  const taskType = pathOr('', ['task_type'], task);
  const isTaskCompleted = equals(status, STATUS_COMPLETED)
  const isLoe = isNotEmpty(pathOr('', ['asks', 'template'], task))
  const shortDescription = pathOr('', [META_DATA, 'lendsmart_sh/task_short_description'], task)
  const requiredAtStageAsString = pathOr('', [META_DATA, 'lendsmart_sh/required_at_stage'], task)
  const requiredAtStage = requiredAtStageAsString ? split(',',requiredAtStageAsString) : []

  return {
    comment,
    createdAt,
    dueDate,
    id,
    status,
    assigneeName,
    assignorName,
    taskType,
    isTaskCompleted,
    documentCategory,
    documentArtifact,
    isLoe,
    shortDescription,
    requiredAtStage
  }
}
//return task title as document category if the task has or  if the task is loe it will return task comment otherwise it will return default text with the assignee name 
export const getTaskTitleOnDashBoard = (taskDetails: any) => {
  const { documentCategory, comment, shortDescription } = taskDetails;
  return cond([
    [() => isNotEmpty(shortDescription), always(capitalizeAllWords(shortDescription))],
    [() => isNotEmpty(documentCategory), always(capitalizeAllWords(documentCategory))],
    [T, always(comment)]
  ])()

}

export const gellAllTaskList = (tasks: any, taskCount: number) => {
  if (!gt(taskCount, 0)) return [];
  const result: any[] = [];

  const pendingTasks = filter(
    (task: any) => {
      const flattened = flattenTask(task)
      return !flattened.isTaskCompleted;
    },
    map(flattenTask, tasks)
  )
  gt(taskCount, 0) && forEach((task: any) => {
    const {
      documentCategory,
      comment,
      shortDescription,
      isTaskCompleted,
      requiredAtStage
    } = task;
    if (!isTaskCompleted) {
      const taskTitle = getTaskTitleOnDashBoard({
        documentCategory,
        comment,
        shortDescription,
      });
      result.push({
        title: taskTitle,
        requiredAtStage,
      })
    }
  }, pendingTasks)

  return result;
};
//This method will return path to sort the tasks based on values of document catgory or short description or comment
export const sortTasks = (tasks:any) => {
  const extractSortingKeys = (task:any) => {
    const priority = (() => {
      const rawPriority = pathOr('', ['metadata', buildLabel('priority')], task);
      return isEmptyOrNil(rawPriority) ? Infinity : Number(rawPriority);
    })(); 
    const category = pathOr('', ['metadata', buildLabel('document_category')], task);
    const shortDescription = pathOr('', ['metadata', buildLabel('task_short_description')], task);
    const comment = pathOr('', ['comment'], task);
    return [priority, category, shortDescription, comment];
  };
  return sortWith([
    ascend(compose(path([0]), extractSortingKeys)),
    ascend(compose(path([1]), extractSortingKeys)),
    ascend(compose(path([2]), extractSortingKeys)),
    ascend(compose(path([3]), extractSortingKeys))
  ])(tasks || []);
};

export const getPermission = (currentPermission: string, permissions: PermissionType[]) => {
  return find(propEq('name', currentPermission), permissions)
}

export const isEnforceStrongPassword = (token: string) => {
  if (isEmptyOrNil(token))
    return MIN_MODERATE_STRONG;
  var base64Url = token.split('.')[1];
  var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
  }).join(''));
  return getStrongMinCharPassword(pathOr([], ['roles'], JSON.parse(jsonPayload)))
}

export const getStrongMinCharPassword = (rolesId: string[]) => {
  // Check user isBanker or not
  const isBanker = isNotEmpty(rolesId) && not(includes(USER_ONBOARD_APPLICANT, rolesId))
  return returnIsNotEmptyOrTrue(isBanker, MIN_STRONG, MIN_MODERATE_STRONG)
}